import React, { useState, useRef, useEffect, Suspense, lazy } from "react";
import { Smile } from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";

const EmojiPicker = lazy(() => import("emoji-picker-react"));

export const EmojiPickerButton = ({
  onEmojiSelect,
  disabled = false,
  position = "top", // "top" | "bottom"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const theme = useTheme();
  const isDark = theme.mode === "dark";

  // Close when clicking outside or pressing Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleEmojiClick = (emojiData) => {
    if (emojiData && emojiData.emoji) {
      onEmojiSelect(emojiData.emoji);
    }
    // Keep open or close? In Slack/chat apps, selecting an emoji typically keeps it open
    // or closes it. Keeping it open lets users insert multiple emojis, but clicking outside closes it.
  };

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Insertar emoji"
        title="Insertar emoji"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "38px",
          height: "38px",
          borderRadius: "8px",
          border: `1px solid ${isOpen ? "#ECB236" : isDark ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1"}`,
          backgroundColor: isOpen
            ? isDark
              ? "rgba(236, 178, 54, 0.15)"
              : "#fef3c7"
            : isDark
            ? "#1e2433"
            : "#f1f5f9",
          color: isOpen ? "#ECB236" : isDark ? "#cbd5e1" : "#475569",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "all 0.15s ease",
          outline: "none",
        }}
        onMouseEnter={(e) => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.color = "#ECB236";
            e.currentTarget.style.borderColor = "#ECB236";
          }
        }}
        onMouseLeave={(e) => {
          if (!isOpen) {
            e.currentTarget.style.color = isDark ? "#cbd5e1" : "#475569";
            e.currentTarget.style.borderColor = isDark ? "rgba(255, 255, 255, 0.15)" : "#cbd5e1";
          }
        }}
      >
        <Smile size={20} />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            zIndex: 1050,
            ...(position === "top"
              ? { bottom: "calc(100% + 8px)", left: 0 }
              : { top: "calc(100% + 8px)", left: 0 }),
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Suspense
            fallback={
              <div
                style={{
                  width: "350px",
                  height: "400px",
                  backgroundColor: isDark ? "#1e222d" : "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isDark ? "#94a3b8" : "#64748b",
                  fontSize: "var(--font-size-min, 15px)",
                  borderRadius: "12px",
                  border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                }}
              >
                Cargando emojis...
              </div>
            }
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              theme={isDark ? "dark" : "light"}
              searchPlaceHolder="Buscar emoji..."
              lazyLoadEmojis={true}
              previewConfig={{ showPreview: false }}
              width={350}
              height={400}
            />
          </Suspense>
        </div>
      )}
    </div>
  );
};
